// การ์ดเอกสารหนึ่งไฟล์
//
// รูปที่เปิดสวิตช์ "พรีวิวในหน้า" ไว้จะโชว์ตัวรูปเลย ที่เหลือโชว์เป็นแถวไฟล์
// (สวิตช์นี้คือสิ่งที่เก็บใน field preview ฝั่ง server ไม่ใช่ค่าชั่วคราวในหน้า)
import { useState } from "react";
import {
  IconDownload,
  IconEye,
  IconFile,
  IconImage,
  IconPencil,
  IconTrash,
} from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";
import { documentFileUrl, type CampaignDocument } from "../api/documents-api";
import { extOf, fileKind, fmtBytes, kindLabel } from "../lib/file-kind";

export function DocumentCard({
  doc,
  busy,
  onOpen,
  onRename,
  onTogglePreview,
  onDelete,
}: {
  doc: CampaignDocument;
  busy: boolean;
  onOpen: () => void;
  onRename: (name: string) => void;
  onTogglePreview: (preview: boolean) => void;
  onDelete: () => void;
}) {
  const kind = fileKind(doc);
  const isImage = kind === "image";
  const showImage = isImage && doc.preview;
  const [editing, setEditing] = useState(false);

  return (
    <div className={cn("doc-card", showImage && "doc-card-img", busy && "doc-card-busy")}>
      {showImage ? (
        <button type="button" className="doc-thumb" onClick={onOpen} title="กดเพื่อดูขนาดเต็ม">
          <img src={documentFileUrl(doc.id)} alt={doc.name} loading="lazy" />
        </button>
      ) : (
        <span className="doc-ext" aria-hidden="true">
          {isImage ? <IconImage size={18} /> : <IconFile size={18} />}
          <em>{extOf(doc.name) || "?"}</em>
        </span>
      )}

      <div className="doc-info">
        {editing ? (
          <RenameForm
            initial={doc.name}
            onCancel={() => setEditing(false)}
            onSave={(name) => {
              setEditing(false);
              if (name !== doc.name) onRename(name);
            }}
          />
        ) : (
          <button type="button" className="doc-name" onClick={onOpen} title={doc.name}>
            {doc.name}
          </button>
        )}
        <div className="doc-meta muted">
          {kindLabel(kind)} · {fmtBytes(doc.size)} ·{" "}
          {new Date(doc.createdAt).toLocaleDateString("th-TH")}
        </div>
      </div>

      <div className="doc-card-actions">
        {isImage && (
          <label
            className="doc-switch"
            title="เปิดไว้ = โชว์ตัวรูปในหน้านี้ / ปิด = โชว์เป็นแถวไฟล์"
          >
            <input
              type="checkbox"
              checked={doc.preview}
              disabled={busy}
              onChange={(e) => onTogglePreview(e.target.checked)}
            />
            <span>พรีวิวในหน้า</span>
          </label>
        )}
        <button type="button" className="icon-btn" title="พรีวิว" onClick={onOpen}>
          <IconEye size={16} />
        </button>
        <button
          type="button"
          className="icon-btn"
          title="เปลี่ยนชื่อ"
          disabled={busy}
          onClick={() => setEditing(true)}
        >
          <IconPencil size={16} />
        </button>
        <a
          className="icon-btn"
          title="ดาวน์โหลด"
          href={documentFileUrl(doc.id, { download: true })}
          download={doc.name}
        >
          <IconDownload size={16} />
        </a>
        <button
          type="button"
          className="icon-btn danger"
          title="ลบ"
          disabled={busy}
          onClick={() => {
            if (confirm(`ลบไฟล์ "${doc.name}"? ลบแล้วเอากลับไม่ได้`)) onDelete();
          }}
        >
          <IconTrash size={16} />
        </button>
      </div>
    </div>
  );
}

function RenameForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <form
      className="doc-rename"
      onSubmit={(e) => {
        e.preventDefault();
        const name = value.trim();
        if (name) onSave(name);
      }}
    >
      {/* ยกเลิกด้วย Esc ไม่ใช้ onBlur เพราะการกดปุ่มบันทึกทำให้ input blur ก่อน submit */}
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
      />
      <button type="submit" className="btn-primary">
        บันทึก
      </button>
      <button type="button" className="btn-outline" onClick={onCancel}>
        ยกเลิก
      </button>
    </form>
  );
}
